import React from 'react';
import { Button, Drawer, Form, Input, Switch, Upload, Image, Select, Spin } from 'antd';
import { Upload as UploadIcon, TriangleAlert } from 'lucide-react';
import { useProductDrawer } from './productDrawer.controller';
import { isFeatureEnabled } from '../../../company/session';
import { FEATURES } from '../../../company/features';
import { t } from '../../../company/labels';

export default function ProductDrawer({ isOpen, setIsOpen, selectedProduct, onSubmit, loading, onUploadImage, searchBarcodes, categoryOptions = [] }) {
    const { form, uploading, uploadError, barcodeOptions, barcodeSearching, handleBarcodeSearch, submit, beforeUpload, customUpload, imageUri } =
        useProductDrawer({ isOpen, selectedProduct, onSubmit, onUploadImage, searchBarcodes });

    return (
        <Drawer
            title={selectedProduct ? 'Editar producto' : 'Nuevo producto'}
            open={isOpen}
            onClose={() => setIsOpen(false)}
            width={480}
            destroyOnClose
            footer={
                <div className="flex justify-end gap-2">
                    <Button onClick={() => setIsOpen(false)}>Cancelar</Button>
                    <Button type="primary" onClick={submit} loading={loading}>
                        Guardar
                    </Button>
                </div>
            }>
            <Form form={form} layout="vertical" initialValues={{ active: true }}>
                <Form.Item label="Nombre" name="name" rules={[{ required: true, message: 'El nombre es requerido' }]}>
                    <Input placeholder="Ej: Arroz Dorado 800g" />
                </Form.Item>
                <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                    <Form.Item label="Código" name="value" rules={[{ required: true, message: 'El código es requerido' }]}>
                        <Input placeholder="Ej: DORADO-0130" />
                    </Form.Item>
                    <Form.Item label="Categoría" name="category" rules={[{ required: true, message: 'La categoría es requerida' }]}>
                        <Select
                            showSearch
                            placeholder="Selecciona una categoría"
                            options={categoryOptions}
                            optionFilterProp="label"
                            notFoundContent="No hay categorías disponibles"
                        />
                    </Form.Item>
                </div>
                {isFeatureEnabled(FEATURES.AUDITOR) && (
                    <Form.Item
                        label="Código de barra (auditoría)"
                        name="auditProductId"
                        tooltip={`Relaciona este ${t('ownBrandProduct')} con un código de barra creado por un auditor. Hereda su segmento y categoría en el Levantamiento General.`}>
                        <Select
                            showSearch
                            allowClear
                            placeholder="Busca por nombre o código de barra"
                            filterOption={false}
                            onSearch={handleBarcodeSearch}
                            options={barcodeOptions}
                            loading={barcodeSearching}
                            notFoundContent={barcodeSearching ? <Spin size="small" /> : 'Escribe para buscar (mín. 2 caracteres)'}
                        />
                    </Form.Item>
                )}

                <div className="mb-6 border-t border-surface-100" />

                <Form.Item label="URL de imagen" name="imageUri">
                    <Input placeholder="https://..." />
                </Form.Item>
                <Form.Item hidden name="imageThumbnail">
                    <Input />
                </Form.Item>
                <Form.Item label="Subir imagen de catálogo">
                    <div className="flex items-start gap-4">
                        {imageUri ? (
                            <Image
                                src={imageUri}
                                alt="Vista previa producto"
                                width={96}
                                height={96}
                                className="rounded-lg border border-surface-200 bg-white object-contain"
                            />
                        ) : null}
                        <div className="min-w-0 flex-1">
                            <Upload accept=".jpg,.jpeg,.png,.webp" maxCount={1} showUploadList={false} beforeUpload={beforeUpload} customRequest={customUpload}>
                                <Button icon={<UploadIcon />} loading={uploading}>
                                    {uploading ? 'Optimizando y subiendo...' : 'Seleccionar imagen'}
                                </Button>
                            </Upload>
                            <p className="m-0 mt-2 text-xs text-surface-500">
                                Recomendado: fondo limpio, proporción cuadrada o vertical. La API reducirá peso y tamaño automáticamente.
                            </p>
                            {uploadError ? (
                                <p className="m-0 mt-2 flex items-center gap-1.5 text-xs text-danger-fg">
                                    <TriangleAlert className="shrink-0" />
                                    {uploadError}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </Form.Item>
                <Form.Item label="Activo" name="active" valuePropName="checked" className="!mb-0">
                    <Switch />
                </Form.Item>
            </Form>
        </Drawer>
    );
}
